import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useContent, resolveImg, isLocalImg } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];
const show = (v) => (v && String(v).trim() ? v : "Sob consulta");

export default function CarregadorDetalhePage() {
  const { id } = useParams();
  const { chargers } = useContent();
  const ch = chargers.find((c) => c.id === id);

  useSeo(
    ch ? `${ch.nome} — Ficha Técnica | HB Charger` : "Carregador | HB Charger",
    ch ? `Ficha técnica do ${ch.nome}: potência, conectores, tensão de saída e comunicação OCPP. Infraestrutura de recarga HB Solar.` : ""
  );

  if (!ch) {
    return (
      <section className="pt-[76px] min-h-[60vh] flex items-center">
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-[#0B192C]">Modelo não encontrado</h1>
          <Link to="/carregadores" className="mt-6 inline-flex items-center gap-2 text-amber-600 font-bold">
            <ArrowLeft className="h-4 w-4" /> Voltar ao catálogo
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative grain overflow-hidden bg-[#0B192C] pt-[76px]">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <Link to="/carregadores" className="inline-flex items-center gap-2 text-sm font-bold text-slate-300 hover:text-amber-400 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Catálogo de carregadores
          </Link>
          <div className="mt-8 grid lg:grid-cols-2 gap-12 items-start pb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="rounded-3xl overflow-hidden border border-white/15 shadow-2xl"
            >
              <img
                src={resolveImg(ch.foto)}
                alt={`${ch.nome} — ${ch.linha}`}
                className={`h-[380px] lg:h-[460px] w-full ${
                  isLocalImg(ch.foto) ? "object-contain bg-white p-6" : "object-cover"
                }`}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-400">{ch.linha}</p>
              <h1 data-testid="datasheet-title" className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                {ch.nome}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-slate-300">{ch.resumo}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  data-testid="datasheet-quote-button"
                  to={`/contato?produto=${encodeURIComponent(ch.nome)}`}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-extrabold px-7 py-3.5 text-sm transition-colors duration-300"
                >
                  Solicitar orçamento
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12"
          >
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#0B192C]">Ficha técnica</h2>
            <dl className="mt-8 grid sm:grid-cols-2 gap-x-10">
              {[
                ["Potência", ch.potencia],
                ["Número de conectores", ch.conectores],
                ["Voltagem", ch.voltagem],
                ["Tensão de saída", ch.tensao],
                ["Comunicação", ch.comunicacao],
                ["Potência configurável", ch.potenciaConfiguravel],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 border-b border-slate-100 py-4">
                  <dt className="text-sm font-semibold text-slate-500">{k}</dt>
                  <dd className={`text-sm font-bold text-right ${v ? "text-[#0B192C]" : "text-amber-600"}`}>{show(v)}</dd>
                </div>
              ))}
            </dl>

            <h3 className="mt-10 font-display text-lg font-bold text-[#0B192C]">Características</h3>
            {ch.caracteristicas && ch.caracteristicas.length > 0 ? (
              <ul className="mt-4 space-y-2.5">
                {ch.caracteristicas.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />
                    {c}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-slate-500">
                Características detalhadas em atualização. Solicite a documentação completa pelo nosso comercial.
              </p>
            )}

            <div className="mt-10 rounded-2xl bg-amber-50 border border-amber-200 p-6">
              <p className="text-sm leading-relaxed text-slate-700">
                <strong className="text-[#0B192C]">Importante:</strong> a potência efetivamente
                disponível para carregamento depende da infraestrutura elétrica do local,
                demanda existente, capacidade de transformadores, proteções, cabos e
                fornecimento da concessionária. Nenhuma potência é garantida sem análise
                técnica prévia.{" "}
                <Link to="/projeto-instalacao" className="font-bold text-amber-600 hover:text-amber-500">
                  Entenda nosso processo
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
